import {
  ArtistProvider,
  DefaultLayout,
  MenuBar,
  useArtistContext,
} from "Components";
import { useDialog } from "Components/DialogProvider";
import { ArtistEventController } from "contexts/ArtistEventContext";
import { MultiDayEventProvider } from "contexts/MultiDayEventContext";
import { lazy, PropsWithChildren, Suspense, useLayoutEffect } from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { GlobalStyles } from "styled";
import { Dashboard } from "./Dashboard";
import { Fans } from "./Fans";
import { HelpCenter } from "./HelpCenter";
import { Reporting } from "./Reporting";
import { SetFan } from "./SetFan";
import { SetLive } from "./SetLive";
import { ShowDetails } from "./SetLive/ShowDetails";
import { Settings } from "./Settings";
import { QrCodeModal } from "./Settings/Components/ArtistQrCode/QrCodeModal";
import STSReport from "./STSReport";

const SetPageEditorRoute = lazy(() => import("./SetPage"));
const SetPageHomeRoute = lazy(() => import("./SetPageHome"));
const SetPageWizardRoute = lazy(() => import("./SetPageWizard"));
const BrandPartnershipsRoute = lazy(() => import("./BrandPartnerships"));

type Props = RouteComponentProps<{ artistId: string }>;

const SetPageLayout = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();

  if (location.pathname.includes("set-bio/wizard")) {
    return (
      <>
        <MenuBar />
        {children}
      </>
    );
  }

  return (
    <DefaultLayout>
      <GlobalStyles grayBG />
      {children}
    </DefaultLayout>
  );
};

const SetPageRoutes = () => {
  const match = useRouteMatch();

  return (
    <SetPageLayout>
      <Suspense fallback={<></>}>
        <Switch>
          <Route path={match.path} component={SetPageHomeRoute} exact={true} />
          <Route
            path={`${match.path}/wizard`}
            component={SetPageWizardRoute}
            exact={true}
          />
          <Route
            path={`${match.path}/editor`}
            component={SetPageEditorRoute}
            exact={true}
          />
        </Switch>
      </Suspense>
    </SetPageLayout>
  );
};

const BrandPartnershipsRoutes = () => {
  const { id: artistGroupId, brandPartnership } = useArtistContext();

  const match = useRouteMatch();

  if (brandPartnership && !brandPartnership.exists()) {
    return <Redirect to={`/${artistGroupId}`} />;
  }

  return (
    <Switch>
      <Route
        path={match.path}
        exact={true}
        render={() => (
          <DefaultLayout>
            <GlobalStyles grayBG />
            <Suspense fallback={<></>}>
              <BrandPartnershipsRoute />
            </Suspense>
          </DefaultLayout>
        )}
      />
    </Switch>
  );
};

export const Routes = ({ match }: Props) => {
  const { dialog } = useDialog();
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <ArtistProvider>
      {dialog?.qrCode && <QrCodeModal />}
      <Switch>
        <Route
          path={`${match?.path}/reporting/set-live/shows/:eventId`}
          render={(props) => (
            <ArtistEventController eventId={props.match.params.eventId}>
              <MultiDayEventProvider>
                <ShowDetails />
              </MultiDayEventProvider>
            </ArtistEventController>
          )}
        />
        <Route path={`${match?.path}/set-live/:tabParam`} component={SetLive} />
        <Route path={`${match?.path}/set-fan`} component={SetFan} />
        <Route path={`${match?.path}/set-bio`} component={SetPageRoutes} />
        <Redirect
          exact
          from={`${match?.path}/set-live`}
          to={`${match?.path}/set-live/upcoming-shows`}
        />
        <Route path={`${match?.path}/help-center`} component={HelpCenter} />
        <Route
          path={`${match?.path}/fans`}
          render={(props) => <Fans match={props.match} />}
        />
        <Route
          path={`${match?.path}/reporting/:tabParam`}
          component={Reporting}
        />
        <Redirect
          exact
          from={`${match?.path}/reporting`}
          to={`${match?.path}/reporting/set-live`}
        />
        <Route path={`${match?.path}/settings`} component={Settings} />
        <Route path={`${match?.path}/sts-report`} component={STSReport} />
        <Route
          path={`${match?.path}/brand-partnerships`}
          component={BrandPartnershipsRoutes}
        />
        <Route path={`${match?.path}/`} component={Dashboard} />
      </Switch>
    </ArtistProvider>
  );
};
